import { type DependencyList, type RefObject, useLayoutEffect } from "react";
import { LAYOUT_BANNER_SELECTOR, syncSiteHeaderStickyTop } from "@/lib/siteHeaderOffset";

/**
 * Misst die echte Unterkante des Site-Headers und schreibt sie als
 * `--eh-datatable-sticky-top`.
 *
 * `getBoundingClientRect().bottom` hängt auch an Bannern über dem Header
 * (Hero-Störung, Sicherheitsmodus): ohne Scroll-/Sibling-Beobachtung bleibt
 * der Wert nach dem ersten Paint stehen, und klebende Köpfe (Montageplan)
 * docken zu früh an. Banner unter dem Header (Update, Urlaubswunsch) schieben
 * zusätzlich das feste Seitenmenü mit.
 */
export function useSiteHeaderStickyOffset(
  headerRef: RefObject<HTMLElement | null>,
  deps: DependencyList = [],
) {
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    let raf = 0;
    let resizeObserver: ResizeObserver;
    const observeBanners = () => {
      document.querySelectorAll(LAYOUT_BANNER_SELECTOR).forEach((node) => {
        resizeObserver.observe(node);
      });
    };
    const syncNow = () => {
      syncSiteHeaderStickyTop(el);
      observeBanners();
    };
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncNow);
    };
    resizeObserver = new ResizeObserver(sync);

    syncNow();

    resizeObserver.observe(el);
    let sibling = el.previousElementSibling;
    while (sibling) {
      resizeObserver.observe(sibling);
      sibling = sibling.previousElementSibling;
    }

    const parent = el.parentElement;
    const mutationObserver = parent
      ? new MutationObserver(sync)
      : null;
    if (parent && mutationObserver) {
      mutationObserver.observe(parent, { childList: true, subtree: false });
    }

    window.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bewusst zusätzliche Sync-Trigger (z. B. Untertitel)
  }, [headerRef, ...deps]);
}
